import React, { useCallback, useMemo, useState } from 'react'
import { Table } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import {
  STATE_AREA_STATUS_LABELS,
  buildStateRowsFromSummary,
  buildStateSummaryRows,
  getTaskCountiesSummary,
  type PlanBotTaskStatus,
  type StateAreaStatus,
  type StateSummaryRow,
  type TaskSummaryInfo,
} from '../api/fetch-carrier-api'
import CountyBreakdownDrawer from './CountyBreakdownDrawer'
import styles from '../PlanManagementV3.module.scss'

const STATUS_PILL_CLASS: Record<StateAreaStatus, string> = {
  success: styles.statusPillSuccess,
  failed: styles.statusPillError,
  partial: styles.statusPillPending,
  pending: styles.statusPillPending,
  cancelled: styles.statusPillNeutral,
}

function StatusPill({ status }: { status: StateAreaStatus }) {
  return (
    <span className={`${styles.statusPill} ${STATUS_PILL_CLASS[status]}`}>
      {STATE_AREA_STATUS_LABELS[status]}
    </span>
  )
}

function buildColumns(
  onStateClick: (state: string) => void
): ColumnsType<StateSummaryRow> {
  return [
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: '30%',
      render: (value: string) => (
        <button
          type="button"
          className={styles.stateLink}
          onClick={() => onStateClick(value)}
        >
          {value}
        </button>
      ),
    },
    {
      title: 'Total Plans',
      dataIndex: 'totalPlans',
      key: 'totalPlans',
      width: '25%',
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '45%',
      render: (status: StateAreaStatus) => <StatusPill status={status} />,
    },
  ]
}

function NestedPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className={styles.nestedPanelWrap}>
      <div className={styles.nestedPanelCard}>{children}</div>
    </div>
  )
}

interface Props {
  taskId: string
  states: string[]
  summaryInfo: TaskSummaryInfo
  taskStatus?: PlanBotTaskStatus | null
}

export default function TaskStateSummaryTable({
  taskId,
  states,
  summaryInfo,
  taskStatus,
}: Props) {
  const [countyDrawerState, setCountyDrawerState] = useState<string | null>(
    null
  )

  // Expanding a task row fetches the state-level rollup — one row per state with
  // the authoritative totalPlans and COMPLETED/PARTIAL/FAILED status. The slim
  // summaryInfo carried on the list response is used only as a fast fallback
  // while this resolves.
  const { data: summary, isLoading } = useQuery({
    queryKey: ['taskCountiesSummary', taskId],
    queryFn: () => getTaskCountiesSummary(taskId),
    enabled: !!taskId,
    staleTime: 60_000,
  })

  const rows = useMemo<StateSummaryRow[]>(() => {
    if (summary?.length) return buildStateRowsFromSummary(summary)
    return buildStateSummaryRows(states, summaryInfo, taskStatus)
  }, [summary, states, summaryInfo, taskStatus])

  const handleStateClick = useCallback((state: string) => {
    setCountyDrawerState(state)
  }, [])

  const columns = useMemo(
    () => buildColumns(handleStateClick),
    [handleStateClick]
  )

  const countyDrawer = (
    <CountyBreakdownDrawer
      taskId={taskId}
      state={countyDrawerState}
      open={countyDrawerState !== null}
      onClose={() => setCountyDrawerState(null)}
    />
  )

  if (rows.length === 0 && !isLoading) {
    return (
      <NestedPanel>
        <p className={styles.nestedEmpty}>No state summary</p>
        {countyDrawer}
      </NestedPanel>
    )
  }

  return (
    <NestedPanel>
      <Table<StateSummaryRow>
        rowKey="state"
        size="small"
        loading={isLoading && rows.length === 0}
        pagination={false}
        columns={columns}
        dataSource={rows}
        indentSize={0}
      />
      {countyDrawer}
    </NestedPanel>
  )
}
